import axios from 'axios';
import type { AIResponse, AIProvider } from '@/types';
import {
  AI_CONFIG,
  APP_CONFIG,
  FALLBACK_RESPONSES,
  GREETINGS,
  GREETING_RESPONSES,
  RATE_LIMIT_CONFIG,
} from '@/constants';

interface RequestQueueItem {
  userMessage: string;
  resolve: (response: AIResponse) => void;
  reject: (error: Error) => void;
  retries: number;
}

class AIService {
  private provider: AIProvider;
  private requestQueue: RequestQueueItem[] = [];
  private isProcessing: boolean = false;
  private lastRequestTime: number = 0;
  private minRequestInterval: number = RATE_LIMIT_CONFIG.MIN_REQUEST_INTERVAL;

  constructor() {
    this.provider = this.determineProvider();
  }

  private determineProvider(): AIProvider {
    if (APP_CONFIG.USE_FALLBACK) {
      return 'fallback';
    }
    if (AI_CONFIG.OPENAI.API_KEY) {
      return 'openai';
    }
    if (AI_CONFIG.GROK.API_KEY) {
      return 'grok';
    }
    return 'fallback';
  }

  async getResponse(userMessage: string): Promise<AIResponse> {
    // Queue the request and process it with rate limiting
    return new Promise<AIResponse>((resolve, reject) => {
      this.requestQueue.push({
        userMessage,
        resolve,
        reject,
        retries: 0,
      });
      this.processQueue();
    });
  }

  private async processQueue(): Promise<void> {
    if (this.isProcessing || this.requestQueue.length === 0) {
      return;
    }

    this.isProcessing = true;

    while (this.requestQueue.length > 0) {
      const item = this.requestQueue[0];

      // Enforce rate limiting
      const now = Date.now();
      const timeSinceLastRequest = now - this.lastRequestTime;
      if (timeSinceLastRequest < this.minRequestInterval) {
        await this.sleep(this.minRequestInterval - timeSinceLastRequest);
      }

      try {
        this.lastRequestTime = Date.now();
        const response = await this.makeRequest(item.userMessage, item.retries);
        this.requestQueue.shift(); // Remove from queue
        item.resolve(response);
      } catch (error) {
        if (this.isRateLimitError(error) && item.retries < RATE_LIMIT_CONFIG.MAX_RETRIES) {
          // Retry with exponential backoff
          item.retries++;
          const backoffDelay = Math.min(
            RATE_LIMIT_CONFIG.INITIAL_BACKOFF * Math.pow(2, item.retries),
            RATE_LIMIT_CONFIG.MAX_BACKOFF
          );
          console.log(
            `Rate limit hit. Retrying in ${backoffDelay}ms (attempt ${item.retries + 1}/${RATE_LIMIT_CONFIG.MAX_RETRIES + 1})`
          );
          await this.sleep(backoffDelay);
          // Keep item in queue for retry
        } else {
          // Max retries exceeded or other error
          this.requestQueue.shift();
          item.reject(
            error instanceof Error
              ? error
              : new Error('Failed to get AI response after multiple retries')
          );
        }
      }
    }

    this.isProcessing = false;
  }

  private async makeRequest(userMessage: string, _retryCount: number): Promise<AIResponse> {
    try {
      switch (this.provider) {
        case 'openai':
          return await this.getOpenAIResponse(userMessage);
        case 'grok':
          return await this.getGrokResponse(userMessage);
        case 'fallback':
        default:
          return this.getFallbackResponse(userMessage);
      }
    } catch (error) {
      if (this.isRateLimitError(error)) {
        throw error; // Let processQueue handle rate limit errors
      }
      console.error('AI Service Error:', error);
      throw error;
    }
  }

  private isRateLimitError(error: unknown): boolean {
    if (axios.isAxiosError(error)) {
      return error.response?.status === 429;
    }
    return false;
  }

  private sleep(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private async getOpenAIResponse(userMessage: string): Promise<AIResponse> {
    try {
      const response = await axios.post(
        AI_CONFIG.OPENAI.ENDPOINT,
        {
          model: AI_CONFIG.OPENAI.MODEL,
          messages: [
            {
              role: 'system',
              content:
                'You are a helpful, friendly AI assistant. Keep responses concise and engaging.',
            },
            {
              role: 'user',
              content: userMessage,
            },
          ],
          max_tokens: 150,
          temperature: 0.7,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${AI_CONFIG.OPENAI.API_KEY}`,
          },
          timeout: 10000,
        }
      );

      const text = response.data.choices[0]?.message?.content?.trim();
      if (!text) {
        throw new Error('Empty response from OpenAI');
      }

      return { text };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('OpenAI API Error:', error.response?.data || error.message);

        if (error.code === 'ECONNABORTED') {
          throw new Error('Request timeout - the AI service took too long to respond');
        } else if (error.response) {
          const status = error.response.status;
          if (status === 429) {
            throw new Error('429 - Rate limit exceeded');
          } else if (status === 401) {
            throw new Error('401 - Invalid API key or authentication failed');
          } else if (status === 403) {
            throw new Error('403 - Access forbidden');
          } else if (status >= 500) {
            throw new Error(`${status} - OpenAI service error`);
          }
        } else if (error.request) {
          throw new Error('Network Error - Unable to reach the AI service');
        }
      }
      throw error;
    }
  }

  private async getGrokResponse(userMessage: string): Promise<AIResponse> {
    try {
      const response = await axios.post(
        AI_CONFIG.GROK.ENDPOINT,
        {
          model: AI_CONFIG.GROK.MODEL,
          messages: [
            {
              role: 'system',
              content:
                'You are a helpful, friendly AI assistant. Keep responses concise and engaging.',
            },
            {
              role: 'user',
              content: userMessage,
            },
          ],
          max_tokens: 150,
          temperature: 0.7,
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${AI_CONFIG.GROK.API_KEY}`,
          },
          timeout: 10000,
        }
      );

      const text = response.data.choices[0]?.message?.content?.trim();
      if (!text) {
        throw new Error('Empty response from Grok');
      }

      return { text };
    } catch (error) {
      if (axios.isAxiosError(error)) {
        console.error('Grok API Error:', error.response?.data || error.message);

        if (error.code === 'ECONNABORTED') {
          throw new Error('Request timeout - the AI service took too long to respond');
        } else if (error.response) {
          const status = error.response.status;
          if (status === 429) {
            throw new Error('429 - Rate limit exceeded');
          } else if (status === 401) {
            throw new Error('401 - Invalid API key or authentication failed');
          } else if (status === 403) {
            throw new Error('403 - Access forbidden');
          } else if (status >= 500) {
            throw new Error(`${status} - Grok service error`);
          }
        } else if (error.request) {
          throw new Error('Network Error - Unable to reach the AI service');
        }
      }
      throw error;
    }
  }

  private getFallbackResponse(userMessage: string): AIResponse {
    const lowerMessage = userMessage.toLowerCase().trim();

    // Handle greetings
    if (GREETINGS.some((greeting) => lowerMessage.includes(greeting))) {
      const randomGreeting =
        GREETING_RESPONSES[Math.floor(Math.random() * GREETING_RESPONSES.length)];
      return { text: randomGreeting };
    }

    // Handle questions
    if (lowerMessage.includes('?')) {
      const responses = [
        "That's a great question! Based on what you're asking, I'd say it depends on the context.",
        'Interesting question! Let me think... I believe the answer involves considering multiple factors.',
        'Good question! From my perspective, there are a few ways to look at this.',
      ];
      return { text: responses[Math.floor(Math.random() * responses.length)] };
    }

    // Handle specific keywords
    if (lowerMessage.includes('help')) {
      return { text: "I'm here to help! Feel free to ask me anything or just chat with me." };
    }

    if (lowerMessage.includes('how are you') || lowerMessage.includes('how r u')) {
      return { text: "I'm doing great, thank you for asking! How are you doing today?" };
    }

    if (lowerMessage.includes('name')) {
      return {
        text: "I'm an AI assistant created to chat with you! You can call me AI. What's your name?",
      };
    }

    if (lowerMessage.includes('bye') || lowerMessage.includes('goodbye')) {
      return { text: 'Goodbye! It was nice chatting with you. Come back anytime!' };
    }

    // Default responses
    const randomResponse =
      FALLBACK_RESPONSES[Math.floor(Math.random() * FALLBACK_RESPONSES.length)];
    return { text: randomResponse };
  }

  getProvider(): AIProvider {
    return this.provider;
  }
}

export const aiService = new AIService();
